
import Card from '../../../components/base/Card';

interface WorkSample {
  id: number;
  title: string;
  description: string;
  result: string;
  image: string;
  tags: string[];
  link: string;
}

export default function WorkSection() {
  const workSamples: WorkSample[] = [
    {
      id: 1,
      title: 'National Mango Board\'s "Mango Joy Ride" Campaign',
      description: 'Took the National Mango Board on the road in an experiential campaign amplified through social media and influencer marketing.',
      result: '2024 MarCom Award Winner',
      image: 'https://static.readdy.ai/image/7e30e953bcb98024829bc0b49eccc224/a6ed91835be58cb4a5830330c1f2803f.png',
      tags: ['Experiential Marketing', 'Social Media', 'Influencer Marketing', 'Award Winner'],
      link: 'https://www.instagram.com/p/C-vlgFbJGiI/'
    },
    {
      id: 2,
      title: 'Downtown Napa\'s Lighted Art Festival',
      description: 'Welcomed travelers to Downtown Napa for its annual Lighted Art Festival, including traffic campaigns in Meta & TikTok and a user-generated content campaign on organic channels.',
      result: 'CTR ~15% higher than Always On campaigns',
      image: 'https://static.readdy.ai/image/7e30e953bcb98024829bc0b49eccc224/88aab4d99e342cf4eee50154d2d3b193.jfif',
      tags: ['Paid Social', 'User-Generated Content', 'Meta Campaigns', 'TikTok Marketing'],
      link: 'https://www.instagram.com/reel/DEgOUnexOls/'
    },
    {
      id: 3,
      title: 'Twelve Cowboys Way Grand Opening',
      description: 'Helped this brand new apartment community build its digital presence and cultivate substantive leads for potential residents.',
      result: 'ROI ~10% better than client benchmark',
      image: 'https://static.readdy.ai/image/7e30e953bcb98024829bc0b49eccc224/9c0b54275984d182c7aa82a85395df88.jfif',
      tags: ['Social Media Community', 'Lead Generation', 'Property Marketing', 'Community Building'],
      link: 'https://www.instagram.com/twelvecowboysway/'
    },
    {
      id: 4,
      title: 'Visit Bastrop Annual Homecoming & Rodeo',
      description: 'Developed vertical videos for Reels (Meta), TikTok and YouTube Shorts marketing tourism to Bastrop, TX, for its annual Homecoming & Rodeo event.',
      result: 'More than 3x Bastrop\'s average organic video viewership',
      image: 'https://static.readdy.ai/image/7e30e953bcb98024829bc0b49eccc224/360575f76f19e497e6a1897bba30cf2e.jfif',
      tags: ['Video Content', 'Tourism Marketing', 'Organic Social', 'Multi-Platform'],
      link: 'https://www.instagram.com/visitbastroptx/reel/C9fps0IK0BH/'
    },
    {
      id: 5,
      title: 'Influencer Trends, As Told From An Agency Perspective',
      description: 'Authored agency blog with perspective on current (at time of publication) influencer marketing trends agencies should consider for their clients.',
      result: 'Enhanced SEO score of agency blog',
      image: 'https://static.readdy.ai/image/7e30e953bcb98024829bc0b49eccc224/79a0b59aa7ca65a0d8dd0bfe2fc35adc.jfif',
      tags: ['Content Marketing', 'Influencer Strategy', 'SEO Optimization', 'Thought Leadership'],
      link: 'https://augustineagency.com/blog/influencer-trends-as-told-from-an-agency-perspective/'
    }
  ];

  return (
    <section 
      id="work" 
      className="py-20 bg-cover bg-center bg-fixed relative"
      style={{
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)), url('https://public.readdy.ai/ai/img_res/4ca7926fa84be252a12fc29f03a0dd65.jpg')`
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Championship Work
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto italic">
            "Champions keep playing until they get it right." - Billie Jean King
          </p>
        </div>
        {/* Work Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {workSamples.map((work) => (
            <Card key={work.id} className="group hover:scale-105 transition-transform duration-300 bg-white/90 backdrop-blur-sm">
              <a 
                href={work.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-lg mb-4">
                  <img 
                    src={work.image}
                    alt={work.title}
                    className={`w-full h-48 object-cover ${work.id === 3 ? 'object-center' : 'object-top'} group-hover:scale-110 transition-transform duration-300`}
                  />
                  <div className="absolute inset-0 bg-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {work.tags.map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100/80 text-blue-700 text-xs rounded-full font-medium backdrop-blur-sm">
                      {tag}
                    </span>
                  ))}
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">{work.title}</h3>
                <p className="text-gray-600 mb-4 text-sm leading-relaxed">{work.description}</p>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900 text-sm flex items-center">
                    <div className="w-4 h-4 flex items-center justify-center mr-2">
                      <i className="ri-trophy-line text-blue-600"></i>
                    </div>
                    Key Result:
                  </h4>
                  <div className="text-sm text-gray-600 flex items-center">
                    <div className="w-3 h-3 flex items-center justify-center mr-2">
                      <i className="ri-check-line text-green-600 text-xs"></i>
                    </div>
                    {work.result}
                  </div>
                </div>
              </a>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
